import React, { useCallback, useContext } from 'react';
import { ScreenableFieldSetupContext } from '../helpers/useScreenableFieldSetupContextManager';
import { ListSelector } from './ListSelector';
import { ApiSanctionType } from '../../../api';
import { useMe } from '../../../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';
import useCustomLists from '../../../custom-lists/useCustomLists';
import { CustomListSelector } from './CustomListSelector';
import { CAN_CONFIGURE_PEP_AM_TRANSACTION_SCREENING } from '../../../auth/permissions';
import Has from '../../../auth/Has';
import { InternalApiEntityType } from '../../../api-internal';

export type ScreenableFieldCustomListConfiguration = {
  list: {
    id: string;
    name: string;
  };
  fields: {
    id: string;
    name: string;
  }[];
};

export const ListSelectionStep = () => {
  const {
    entityType,
    dowJonesListTypes,
    customListConfiguration,
    updateDowJonesListTypes,
    updateCustomListConfiguration,
    updateSelectorGroupCodes,
  } = useContext(ScreenableFieldSetupContext);

  const customLists = useCustomLists();
  const { features } = useMe();
  const isAdverseMediaEntitiesEnabled = isFeatureEnabled(ORGANISATION_FEATURE.ADVERSE_MEDIA_ENTITIES, features);

  const toggleListType = useCallback(
    (type: ApiSanctionType) => (isToggled: boolean) => {
      if (isToggled) {
        updateDowJonesListTypes([...dowJonesListTypes, type]);
        if (type === ApiSanctionType.SANCTION) {
          updateSelectorGroupCodes([]);
        }
      } else {
        updateDowJonesListTypes(dowJonesListTypes.filter((it) => it !== type));
        if (type === ApiSanctionType.SANCTION) {
          updateSelectorGroupCodes(undefined);
        }
      }
    },
    [dowJonesListTypes, updateDowJonesListTypes, updateSelectorGroupCodes]
  );

  const toggleCustomList = useCallback(
    (listId: string, listName: string) => (isToggled: boolean) => {
      if (isToggled) {
        updateCustomListConfiguration([
          ...customListConfiguration,
          {
            list: {
              id: listId,
              name: listName,
            },
            fields: [],
          },
        ]);
      } else {
        updateCustomListConfiguration(customListConfiguration.filter((it) => it.list.id !== listId));
      }
    },
    [customListConfiguration, updateCustomListConfiguration]
  );

  function renderPEPAndAdverseMedia(isAdverseMediaEntitiesEnabled: boolean) {
    return (
      <>
        <ListSelector
          title='Dow Jones PEP'
          isSelected={dowJonesListTypes.includes(ApiSanctionType.PEP)}
          onChange={toggleListType(ApiSanctionType.PEP)}
        />
        <ListSelector
          title='Dow Jones Adverse Media'
          isSelected={dowJonesListTypes.includes(ApiSanctionType.ADVERSE_MEDIA)}
          onChange={toggleListType(ApiSanctionType.ADVERSE_MEDIA)}
        />
        {isAdverseMediaEntitiesEnabled && (
          <ListSelector
            title='Dow Jones Adverse Media Entities'
            isSelected={dowJonesListTypes.includes(ApiSanctionType.ADVERSE_MEDIA_ENTITY)}
            onChange={toggleListType(ApiSanctionType.ADVERSE_MEDIA_ENTITY)}
          />
        )}
      </>
    );
  }

  function isAlreadyUsingPEPOrAdverseMedia(dowJonesListTypes: ApiSanctionType[]) {
    return (
      dowJonesListTypes.includes(ApiSanctionType.PEP) ||
      dowJonesListTypes.includes(ApiSanctionType.ADVERSE_MEDIA) ||
      dowJonesListTypes.includes(ApiSanctionType.ADVERSE_MEDIA_ENTITY)
    );
  }

  return (
    <div className='p-a-24'>
      <div className='list-selection-step'>
        <h4 className='text-strong-m chapter-title'>Screened against</h4>
        <span className='text-s chapter-subtitle m-t-4'>Choose the lists you want to screen your data against.</span>
        <ListSelector
          title='Dow Jones Sanctions'
          isSelected={dowJonesListTypes.includes(ApiSanctionType.SANCTION)}
          onChange={toggleListType(ApiSanctionType.SANCTION)}
        />
        {entityType == InternalApiEntityType.PERSON || isAlreadyUsingPEPOrAdverseMedia(dowJonesListTypes) ? (
          renderPEPAndAdverseMedia(isAdverseMediaEntitiesEnabled)
        ) : (
          <Has allPermissions={CAN_CONFIGURE_PEP_AM_TRANSACTION_SCREENING}>
            {renderPEPAndAdverseMedia(isAdverseMediaEntitiesEnabled)}
          </Has>
        )}
        {customLists
          .filter((it) => !it.code?.startsWith('DOW_JONES'))
          .map((list) => (
            <CustomListSelector
              key={list.id}
              title={list.name}
              isSelected={customListConfiguration.some((conf) => conf.list?.id === list.id)}
              onChange={toggleCustomList(list.id!, list.name)}
              listId={list.id!}
            />
          ))}
      </div>
    </div>
  );
};
